import { clamp01 } from '../../core/math';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import type { RenderTarget, TechniqueContext } from '../types';
import type { RectPackingValues } from './params';

/**
 * Binary space partition of the negative space.
 *
 * Rectangles are split recursively, and the text field decides where to keep going:
 * a rectangle near a letterform is subdivided further even when the random test says
 * stop. That is what makes the pattern *describe* the type — detail concentrates along
 * the contours, coarse blocks fill the open field — rather than being a texture that
 * happens to sit behind it.
 */
export interface PackedRect {
  x: number;
  y: number;
  width: number;
  height: number;
  depth: number;
  distance: number;
  tint: number;
}

export interface RectPackingState {
  rects: PackedRect[];
  /** Rectangles still to consider, as a flat stack. */
  pending: { x: number; y: number; width: number; height: number; depth: number }[];
  palette: ResolvedPalette;
  maxArea: number;
  maxDepth: number;
  done: boolean;
}

function values(ctx: TechniqueContext): RectPackingValues {
  return ctx.values as unknown as RectPackingValues;
}

export function init(ctx: TechniqueContext): RectPackingState {
  const v = values(ctx);
  return {
    rects: [],
    pending: [{ x: 0, y: 0, width: ctx.width, height: ctx.height, depth: 0 }],
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    maxArea: ctx.width * ctx.height,
    maxDepth: Math.max(1, v.depth),
    done: false,
  };
}

export function step(state: RectPackingState, _dt: number, ctx: TechniqueContext): RectPackingState {
  if (state.done) return state;
  const v = values(ctx);
  const budget = Math.max(200, Math.round(4000 * clamp01(ctx.quality)));

  for (let n = 0; n < budget; n++) {
    const node = state.pending.pop();
    if (!node) {
      state.done = true;
      break;
    }

    const cx = node.x + node.width / 2;
    const cy = node.y + node.height / 2;
    const signed = ctx.field.distance(cx, cy);

    // Refinement: a rectangle straddling the contour keeps splitting.
    const nearText =
      v.textRefinement > 0 && Math.abs(signed) < v.refinementReach
        ? 1 - clamp01(Math.abs(signed) / v.refinementReach)
        : 0;
    const splitChance = Math.min(1, v.splitChance + nearText * v.textRefinement);

    const tooSmall = node.width < v.minSize * 2 || node.height < v.minSize * 2;
    const deepEnough = node.depth >= state.maxDepth;

    if (tooSmall || deepEnough || !ctx.rng.chance(splitChance)) {
      // Keep it, if it belongs to the selected region.
      const keep =
        v.region === 'all' ||
        (v.region === 'outside' && signed > 0) ||
        (v.region === 'inside' && signed < 0);
      if (keep) {
        state.rects.push({
          x: node.x,
          y: node.y,
          width: node.width,
          height: node.height,
          depth: node.depth,
          distance: signed,
          tint: ctx.rng.next(),
        });
      }
      continue;
    }

    // Cut the longer side with probability `aspectBias`, otherwise the other one.
    const cutVertical = node.width > node.height ? ctx.rng.chance(v.aspectBias) : !ctx.rng.chance(v.aspectBias);
    const t = 0.5 + ctx.rng.signed(v.splitJitter);

    if (cutVertical) {
      const w = node.width * t;
      state.pending.push({ x: node.x, y: node.y, width: w, height: node.height, depth: node.depth + 1 });
      state.pending.push({ x: node.x + w, y: node.y, width: node.width - w, height: node.height, depth: node.depth + 1 });
    } else {
      const h = node.height * t;
      state.pending.push({ x: node.x, y: node.y, width: node.width, height: h, depth: node.depth + 1 });
      state.pending.push({ x: node.x, y: node.y + h, width: node.width, height: node.height - h, depth: node.depth + 1 });
    }
  }

  return state;
}

export function isComplete(state: RectPackingState): boolean {
  return state.done;
}

function traceRect(path: Path2D, x: number, y: number, w: number, h: number, radius: number): void {
  const r = Math.min(radius, w / 2, h / 2);
  if (r <= 0) {
    path.rect(x, y, w, h);
    return;
  }
  path.moveTo(x + r, y);
  path.lineTo(x + w - r, y);
  path.arcTo(x + w, y, x + w, y + r, r);
  path.lineTo(x + w, y + h - r);
  path.arcTo(x + w, y + h, x + w - r, y + h, r);
  path.lineTo(x + r, y + h);
  path.arcTo(x, y + h, x, y + h - r, r);
  path.lineTo(x, y + r);
  path.arcTo(x, y, x + r, y, r);
  path.closePath();
}

export function draw(state: RectPackingState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c = target.ctx;
  const reach = Math.max(1, ctx.field.maxOutside);
  c.lineWidth = v.lineWidth;
  c.lineJoin = 'round';

  const fills = new Map<string, Path2D>();
  const strokes = new Map<string, Path2D>();

  for (const rect of state.rects) {
    const w = rect.width - v.gap;
    const h = rect.height - v.gap;
    if (w <= 0.5 || h <= 0.5) continue;

    let t: number;
    switch (v.colorMode) {
      case 'size':
        t = clamp01((rect.width * rect.height) / (state.maxArea * 0.04));
        break;
      case 'depth':
        t = rect.depth / state.maxDepth;
        break;
      case 'distance':
        t = clamp01(Math.abs(rect.distance) / reach);
        break;
      case 'random':
        t = rect.tint;
        break;
      default:
        t = 0;
    }
    const color = v.colorMode === 'solid' ? state.palette.stop(0, v.opacity) : state.palette.at(t, v.opacity);
    const filled = rect.tint < v.fillChance;
    const map = filled ? fills : strokes;
    let path = map.get(color);
    if (!path) {
      path = new Path2D();
      map.set(color, path);
    }
    traceRect(path, rect.x + v.gap / 2, rect.y + v.gap / 2, w, h, v.radius);
  }

  for (const [color, path] of fills) {
    c.fillStyle = color;
    c.fill(path);
  }
  for (const [color, path] of strokes) {
    c.strokeStyle = color;
    c.stroke(path);
  }
}

export function elementCount(state: RectPackingState): number {
  return state.rects.length;
}
